import React, { useState } from "react";
import styled from "styled-components";
import { 
  Users, 
  Award, 
  TrendingUp,
  Zap,
  Globe,
  Shield,
  ArrowRight,
  Star,
  Check
} from "lucide-react";
import { PrimaryButton, Container, Section, FlexContainer, Badge } from "../styles/GlobalStyles";

// Enhanced Hero Section with Modern Design
const HeroSection = styled(Section)`
  min-height: 100vh;
  background: linear-gradient(135deg, 
    var(--primary-600) 0%, 
    var(--primary-700) 25%,
    var(--accent-600) 75%, 
    var(--secondary-600) 100%
  );
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 0;
  margin-top: 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.06) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(to top, var(--gray-0) 0%, transparent 100%);
    pointer-events: none;
  }
`;

const HeroContainer = styled(Container)`
  position: relative;
  z-index: 2;
  height: 100vh;
  display: flex;
  align-items: center;
  padding-top: 80px; /* Account for fixed header */
  
  @media (max-width: 1024px) {
    padding-top: 75px;
    min-height: 90vh;
  }
  
  @media (max-width: 768px) {
    padding-top: 70px;
    min-height: 80vh;
    height: auto;
    padding-bottom: var(--space-8);
  }
  
  @media (max-width: 480px) {
    padding-top: 60px;
    min-height: 75vh;
  }
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: var(--space-16);
  align-items: center;
  width: 100%;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: var(--space-8);
  }
  
  @media (max-width: 768px) {
    gap: var(--space-6);
  }
  
  @media (max-width: 480px) {
    gap: var(--space-4);
  }
`;

const HeroText = styled.div`
  color: var(--gray-0);
  
  .hero-badge {
    margin-bottom: var(--space-6);
    
    @media (max-width: 768px) {
      margin-bottom: var(--space-4);
    }
    
    @media (max-width: 480px) {
      margin-bottom: var(--space-3);
    }
  }
  
  h1 {
    font-size: var(--text-7xl);
    font-weight: 900;
    margin-bottom: var(--space-6);
    line-height: var(--leading-none);
    color: var(--gray-0);
    letter-spacing: -0.05em;
    
    .gradient-text {
      background: linear-gradient(135deg, #60a5fa 0%, #34d399 50%, #fbbf24 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: float 3s ease-in-out infinite;
    }
    
    @media (max-width: 1024px) {
      font-size: var(--text-6xl);
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-4xl);
      margin-bottom: var(--space-4);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-3xl);
      margin-bottom: var(--space-3);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
    opacity: 0.95;
    line-height: var(--leading-relaxed);
    color: rgba(255, 255, 255, 0.9);
    font-weight: 400;
    
    @media (max-width: 1024px) {
      font-size: var(--text-lg);
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-base);
      margin-bottom: var(--space-6);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-sm);
      margin-bottom: var(--space-4);
    }
  }
`;

const ButtonGroup = styled(FlexContainer)`
  margin-bottom: var(--space-10);
  gap: var(--space-4);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--space-8);
    gap: var(--space-3);
    width: 100%;
  }
  
  @media (max-width: 480px) {
    margin-bottom: var(--space-6);
    gap: var(--space-2);
  }
`;

const PlayButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  background: rgba(255, 255, 255, 0.1);
  color: var(--gray-0);
  border: 2px solid rgba(255, 255, 255, 0.2);
  padding: var(--space-4) var(--space-6);
  border-radius: var(--radius-full);
  font-weight: 600;
  transition: all var(--transition-normal);
  backdrop-filter: blur(10px);
  
  .play-icon {
    width: 20px;
    height: 20px;
    background: var(--gray-0);
    color: var(--primary-600);
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-2px);
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-8);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-4);
  }
`;

const StatCard = styled.div`
  text-align: left;
  
  @media (max-width: 968px) {
    text-align: center;
  }
  
  .number {
    font-size: var(--text-4xl);
    font-weight: 800;
    color: #60a5fa;
    display: block;
    line-height: 1;
    margin-bottom: var(--space-1);
    
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
    }
  }
  
  .label {
    font-size: var(--text-sm);
    opacity: 0.9;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
  }
`;

const HeroVisual = styled.div`
  position: relative;
`;

const InteractiveDemo = styled.div`
  background: var(--gray-0);
  border-radius: var(--radius-3xl);
  padding: var(--space-8);
  box-shadow: var(--shadow-2xl);
  position: relative;
  overflow: hidden;
  transform: perspective(1000px) rotateY(-10deg) rotateX(5deg);
  transition: all var(--transition-slow);
  
  &:hover {
    transform: perspective(1000px) rotateY(-5deg) rotateX(2deg) scale(1.02);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: var(--gradient-rainbow);
  }
`;

const DemoTabs = styled.div`
  display: flex;
  gap: var(--space-1);
  margin-bottom: var(--space-6);
  background: var(--gray-100);
  padding: var(--space-1);
  border-radius: var(--radius-lg);
`;

const DemoTab = styled.button`
  flex: 1;
  padding: var(--space-3) var(--space-4);
  border: none;
  background: ${props => props.active ? 'var(--gray-0)' : 'transparent'};
  color: ${props => props.active ? 'var(--gray-900)' : 'var(--gray-600)'};
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: var(--text-sm);
  transition: all var(--transition-fast);
  box-shadow: ${props => props.active ? 'var(--shadow-sm)' : 'none'};
  
  &:hover {
    color: var(--gray-900);
  }
`;

const DemoContent = styled.div`
  min-height: 300px;
  display: flex;
  flex-direction: column;
  
  .demo-header {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-6);
    
    .icon {
      width: 48px;
      height: 48px;
      background: var(--gradient-primary);
      border-radius: var(--radius-xl);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--gray-0);
    }
    
    .info {
      h3 {
        font-size: var(--text-xl);
        color: var(--gray-900);
        margin-bottom: var(--space-1);
      }
      
      p {
        color: var(--gray-600);
        margin: 0;
        font-size: var(--text-sm);
      }
    }
  }
  
  .features-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-4);
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
  
  .feature-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3);
    background: var(--gray-50);
    border-radius: var(--radius-lg);
    transition: all var(--transition-fast);
    
    &:hover {
      background: var(--primary-50);
    }
    
    .check {
      color: var(--secondary-600);
      font-weight: bold;
      flex-shrink: 0;
    }
    
    .text {
      font-size: var(--text-sm);
      color: var(--gray-700);
      font-weight: 500;
    }
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
  
  @media (max-width: 768px) {
    display: none;
  }
  
  .floating-icon {
    position: absolute;
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: var(--radius-2xl);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    animation: float 4s ease-in-out infinite;
    
    &:nth-child(1) {
      top: 20%;
      right: 10%;
      animation-delay: 0s;
    }
    
    &:nth-child(2) {
      top: 50%;
      right: 5%;
      animation-delay: 1s;
    }
    
    &:nth-child(3) {
      top: 70%;
      right: 15%;
      animation-delay: 2s;
    }
  }
`;

// Demo content data
const demoTabs = [
  {
    id: 'willovateresto',
    label: 'WillovateResto',
    icon: <TrendingUp size={24} />,
    title: 'Restaurant IMS',
    description: 'Complete Inventory Management System for restaurants',
    features: [
      'Real-time Inventory Tracking',
      'Menu Management',
      'Order Processing',
      'Cost Control',
      'Supplier Management',
      'Analytics & Reports'
    ]
  },
  {
    id: 'custom',
    label: 'Custom Apps',
    icon: <Users size={24} />,
    title: 'Custom Development',
    description: 'Tailored software solutions using React, .NET, and more',
    features: [
      'React & .NET Applications',
      'Database Design',
      'API Integration',
      'Mobile Responsive',
      'User Authentication',
      'Business Logic'
    ]
  },
  {
    id: 'startup',
    label: 'Startup Ready',
    icon: <Zap size={24} />,
    title: 'Growing with You',
    description: 'Scalable solutions as your business grows',
    features: [
      'MVP Development',
      'Iterative Approach',
      'Cost-Effective',
      'Modern Tech Stack',
      'Ongoing Support',
      'Future Scalability'
    ]
  }
];

const Hero = () => {
  const [currentTab, setCurrentTab] = useState('enterprise');

  const currentDemo = demoTabs.find(tab => tab.id === currentTab) || demoTabs[0];

  return (
    <HeroSection>
      <HeroContainer>
        <HeroContent>
          <HeroText>
            <Badge className="hero-badge primary">
              <Star size={12} />
              Professional Software Development
            </Badge>
            
            <h1>
              Custom <span className="gradient-text">Software Development</span> for Every Industry
            </h1>
            
            <p className="subtitle">
              We're a software development company that builds applications for businesses across various industries. 
              From restaurant management systems like WillovateResto.com to custom web applications, 
              we turn your ideas into powerful software solutions.
            </p>
            
            <ButtonGroup>
              <PrimaryButton onClick={() => window.location.href = 'mailto:admin@willovate.in?subject=Project Inquiry&body=Hello, I would like to discuss a project with Willovate.'}>
                Contact Us
                <ArrowRight size={18} />
              </PrimaryButton>
              <PlayButton onClick={() => window.open('https://willovateresto.com', '_blank')}>
                <div className="play-icon">
                  <ArrowRight size={12} />
                </div>
                View WillovateResto
              </PlayButton>
            </ButtonGroup>

            <StatsContainer>
              <StatCard>
                <span className="number">2025</span>
                <span className="label">Founded</span>
              </StatCard>
              <StatCard>
                <span className="number">Multiple</span>
                <span className="label">Industries</span>
              </StatCard>
              <StatCard>
                <span className="number">1+</span>
                <span className="label">Live Apps</span>
              </StatCard>
            </StatsContainer>
          </HeroText>

          <HeroVisual>
            <InteractiveDemo>
              <DemoTabs>
                {demoTabs.map((tab) => (
                  <DemoTab
                    key={tab.id}
                    active={currentTab === tab.id}
                    onClick={() => setCurrentTab(tab.id)}
                  >
                    {tab.label}
                  </DemoTab>
                ))}
              </DemoTabs>
              
              <DemoContent>
                <div className="demo-header">
                  <div className="icon">
                    {currentDemo.icon}
                  </div>
                  <div className="info">
                    <h3>{currentDemo.title}</h3>
                    <p>{currentDemo.description}</p>
                  </div>
                </div>
                
                <div className="features-grid">
                  {currentDemo.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <Check size={16} className="check" />
                      <span className="text">{feature}</span>
                    </div>
                  ))}
                </div>
              </DemoContent>
            </InteractiveDemo>
          </HeroVisual>
        </HeroContent>
      </HeroContainer>
      
      <FloatingElements>
        <div className="floating-icon">
          <Globe size={24} />
        </div>
        <div className="floating-icon">
          <Shield size={24} />
        </div>
        <div className="floating-icon">
          <Award size={24} />
        </div>
      </FloatingElements>
    </HeroSection>
  );
};

export default Hero;
